import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../ui/Icon';
import Logo from '../ui/Logo';
import { authActions } from '../../store';

const AuthModal = () => {
  const dispatch = useDispatch();
  const { showAuthModal } = useSelector(s => s.auth);
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);

  const handleOAuth = (provider) => {
    // Simulate OAuth login
    dispatch(authActions.login({
      name: 'Alex Rivera',
      email: 'alex@example.com',
      avatar: null,
      provider,
    }));
  };

  const handleMagicLink = (e) => {
    e.preventDefault();
    if (email) setSent(true);
  };

  if (!showAuthModal) return null;

  const oauthProviders = [
    { id: 'google', label: 'Continue with Google', color: '#4285F4', icon: '🔵' },
    { id: 'microsoft', label: 'Continue with Microsoft', color: '#00A4EF', icon: '🟦' },
    { id: 'apple', label: 'Continue with Apple', color: '#000000', icon: '🍎' },
  ];

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[90] flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm"
          onClick={() => { dispatch(authActions.setShowAuthModal(false)); setSent(false); setEmail(''); }} />
        
        <motion.div
          initial={{ opacity: 0, scale: 0.92, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.92, y: 20 }}
          transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="relative w-full max-w-md rounded-3xl overflow-hidden"
          style={{ background: 'var(--bg-primary)', boxShadow: 'var(--shadow-xl)' }}
        >
          {/* Decorative top gradient */}
          <div className="h-1.5 w-full" style={{ background: 'linear-gradient(90deg, #3374ff, #8b5cf6, #f9603e)' }} />
          
          <div className="p-8">
            {/* Close */}
            <button onClick={() => { dispatch(authActions.setShowAuthModal(false)); setSent(false); }}
              className="absolute top-5 right-5 p-1.5 rounded-lg hover:bg-black/5 transition-colors">
              <Icon name="X" size={18} style={{ color: 'var(--text-muted)' }} />
            </button>

            <div className="text-center mb-8">
              <div className="flex justify-center mb-4">
                <Logo size="md" showText={false} />
              </div>
              <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>Welcome to DocuVerse</h2>
              <p className="mt-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                Sign in to unlock unlimited document processing
              </p>
            </div>

            {!sent ? (
              <>
                {/* OAuth Buttons */}
                <div className="space-y-3 mb-6">
                  {oauthProviders.map(p => (
                    <button key={p.id} onClick={() => handleOAuth(p.id)}
                      className="w-full flex items-center justify-center gap-3 px-4 py-3.5 rounded-xl text-sm font-semibold transition-all duration-200 hover:shadow-md"
                      style={{
                        background: 'var(--bg-secondary)',
                        border: '1px solid var(--border-light)',
                        color: 'var(--text-primary)',
                      }}>
                      <span className="text-lg">{p.icon}</span>
                      {p.label}
                    </button>
                  ))}
                </div>

                {/* Divider */}
                <div className="flex items-center gap-3 mb-6">
                  <div className="flex-1 h-px" style={{ background: 'var(--border-light)' }} />
                  <span className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>or use email</span>
                  <div className="flex-1 h-px" style={{ background: 'var(--border-light)' }} />
                </div>

                {/* Magic Link */}
                <form onSubmit={handleMagicLink}>
                  <div className="relative mb-4">
                    <Icon name="Mail" size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
                    <input
                      type="email"
                      placeholder="Enter your email address"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      className="input-field !pl-11"
                      required
                    />
                  </div>
                  <button type="submit" className="btn-primary w-full !py-3.5">
                    <Icon name="Sparkles" size={16} className="mr-2" />
                    Send Magic Link
                  </button>
                </form>
              </>
            ) : (
              /* Magic Link Sent */
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-center py-4">
                <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
                  style={{ background: 'rgba(16,185,129,0.1)' }}>
                  <Icon name="Mail" size={28} style={{ color: '#10b981' }} />
                </div>
                <h3 className="text-lg font-bold mb-2" style={{ color: 'var(--text-primary)' }}>Check your inbox!</h3>
                <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
                  We sent a magic link to <strong>{email}</strong>
                </p>
                <button onClick={() => setSent(false)} className="text-sm font-medium" style={{ color: '#3374ff' }}>
                  Use a different email
                </button>
              </motion.div>
            )}

            {/* Footer */}
            <p className="text-center text-xs mt-6" style={{ color: 'var(--text-muted)' }}>
              By continuing, you agree to our{' '}
              <a href="#" className="underline">Terms</a> and{' '}
              <a href="#" className="underline">Privacy Policy</a>
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AuthModal;
